import React, { useState, useEffect } from 'react';
import { Save, Calendar, IndianRupee } from 'lucide-react';

const API_URL = `/api/rates`;

const FuelRates = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [rates, setRates] = useState({ petrol: 0, diesel: 0, xtragreen: 0 });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_URL}?date=${date}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        setRates({
          petrol: parseFloat(data.petrol) || 0,
          diesel: parseFloat(data.diesel) || 0,
          xtragreen: parseFloat(data.xtragreen) || 0
        });
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [date]);

  const handleSave = () => {
    fetch(API_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        date,
        petrol: rates.petrol,
        diesel: rates.diesel,
        xtragreen: rates.xtragreen
      })
    })
    .then(res => res.json())
    .then(data => {
      if(data.status === 'success') {
        alert("Rates saved successfully!");
      } else {
        alert("Error saving rates.");
      }
    })
    .catch(console.error);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fuel Rates (RSP)</h1>
          <p className="text-slate-500 text-sm mt-1">Update daily Retail Selling Prices</p>
        </div>
        <button onClick={handleSave} className="btn-primary flex items-center gap-2 px-6">
          <Save className="w-5 h-5" /> Save Rates
        </button>
      </div>

      <div className="glass-panel p-6 flex items-center gap-4 mb-6">
        <Calendar className="w-8 h-8 text-indigo-500 shrink-0" />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date</label>
          <input 
            type="date" 
            className="input-field max-w-[200px]"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="hidden sm:block ml-auto bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-medium border border-indigo-200">
          {isLoading ? 'Loading...' : `These prices will apply to all sales on ${date}`}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Petrol */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
          <div className="flex justify-between items-start mb-6 pl-3">
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Petrol (MS)</h3>
            </div>
            <span className="text-xs bg-orange-50 text-orange-600 px-2 py-1 rounded font-bold border border-orange-100">
              ₹/Liter
            </span>
          </div>
          <div className="pl-3 relative">
            <span className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xl">₹</span>
            <input 
              type="number" 
              step="0.01"
              className="input-field pl-10 text-2xl font-mono font-bold text-slate-900 h-16 bg-slate-50 border-slate-200 focus:border-orange-500 focus:ring-orange-500/20"
              value={rates.petrol || ''}
              onChange={(e) => setRates({...rates, petrol: parseFloat(e.target.value)})}
            />
          </div>
        </div>

        {/* Diesel */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
          <div className="flex justify-between items-start mb-6 pl-3">
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Diesel (HSD)</h3>
            </div>
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded font-bold border border-blue-100">
              ₹/Liter
            </span>
          </div>
          <div className="pl-3 relative">
            <span className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xl">₹</span>
            <input 
              type="number" 
              step="0.01"
              className="input-field pl-10 text-2xl font-mono font-bold text-slate-900 h-16 bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
              value={rates.diesel || ''}
              onChange={(e) => setRates({...rates, diesel: parseFloat(e.target.value)})}
            />
          </div>
        </div>

        {/* Xtragreen */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
          <div className="flex justify-between items-start mb-6 pl-3">
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Xtragreen (XG)</h3>
            </div>
            <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-1 rounded font-bold border border-emerald-100">
              ₹/Liter
            </span>
          </div>
          <div className="pl-3 relative">
            <span className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xl">₹</span>
            <input 
              type="number" 
              step="0.01"
              className="input-field pl-10 text-2xl font-mono font-bold text-slate-900 h-16 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
              value={rates.xtragreen || ''}
              onChange={(e) => setRates({...rates, xtragreen: parseFloat(e.target.value)})}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default FuelRates;
